import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  TextField,
  InputAdornment,
  Button,
  Divider,
  CircularProgress,
} from "@mui/material";
import { Thermostat, CheckCircleOutline } from "@mui/icons-material";
import { updateDeviceThresholds } from "../../API/deviceThresholds";

// Réglage des seuils de COLORATION (jaune/rouge) de température, par capteur.
// Indépendant des alertes SMS : le backend préserve l'état SMS existant quand on
// ne fait que changer une valeur de seuil (voir device_thresholds.py). On envoie
// donc alerts_sms:false sans risque de désactiver un SMS déjà configuré.
// Logique de coloration (identique au backend) : valeur > rouge => rouge,
// valeur > jaune => jaune, sinon vert. Direction uniforme (au-dessus = alerte),
// valable aussi pour les chambres froides (seuils négatifs).

function TemperatureThresholdRow({ device, initial, onSaved, isMobile }) {
  const [yellow, setYellow] = useState(initial?.yellow ?? "");
  const [red, setRed] = useState(initial?.red ?? "");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // Resynchroniser les champs si les seuils chargés changent (fetch parent).
  useEffect(() => {
    setYellow(initial?.yellow ?? "");
    setRed(initial?.red ?? "");
  }, [initial?.yellow, initial?.red]);

  const initialYellow = initial?.yellow ?? null;
  const initialRed = initial?.red ?? null;
  const isDirty =
    (yellow === "" ? null : Number(yellow)) !== initialYellow ||
    (red === "" ? null : Number(red)) !== initialRed;

  const handleSave = async () => {
    setSaving(true);
    try {
      // On conserve le temperature_type existant du device (classification faite
      // sur la page de configuration). Défaut 'ambiante' si non renseigné.
      const tempType = initial?.temperature_type || "ambiante";
      const rows = [
        {
          temperature_type: tempType,
          threshold_type: "yellow",
          device_type: "temperature",
          threshold_value: Number(yellow) || 0,
          unit: "celsius",
          alerts_email: true,
          alerts_sms: false,
        },
        {
          temperature_type: tempType,
          threshold_type: "red",
          device_type: "temperature",
          threshold_value: Number(red) || 0,
          unit: "celsius",
          alerts_email: true,
          alerts_sms: false,
        },
      ];
      // postData (axios) renvoie la réponse HTTP complète : le succès se lit sur
      // le code HTTP 200 (resp.status), et le body est dans resp.data.status.
      const resp = await updateDeviceThresholds(device.dev_eui, rows);
      const ok = resp?.status === 200 || resp?.data?.status === "success";
      if (ok) {
        setSaved(true);
        setTimeout(() => setSaved(false), 2000);
        onSaved?.(device.dev_eui, {
          yellow: yellow === "" ? null : Number(yellow),
          red: red === "" ? null : Number(red),
          temperature_type: tempType,
        });
      } else {
        const msg =
          resp?.response?.data?.detail || resp?.message || "Erreur inconnue";
        alert(`Erreur lors de la sauvegarde du seuil : ${msg}`);
      }
    } finally {
      setSaving(false);
    }
  };

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      fontSize: "0.9rem",
      backgroundColor: "var(--bg-card)",
      "& fieldset": { borderColor: "var(--border-medium)" },
    },
    "& .MuiInputLabel-root": { fontSize: "0.82rem" },
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1.25 }}>
        <Thermostat sx={{ fontSize: 18, color: "var(--kpi-rail-temp, #99DCFF)" }} />
        <Typography sx={{ fontSize: "0.95rem", fontWeight: 600, color: "var(--text-primary)" }}>
          {device.name || device.dev_eui}
        </Typography>
      </Box>

      <Grid container spacing={1.5} alignItems="center">
        <Grid item xs={6} sm={4}>
          <TextField
            label="Seuil jaune"
            type="number"
            value={yellow}
            onChange={(e) => setYellow(e.target.value)}
            size="small"
            fullWidth
            sx={{ ...fieldSx, "& .MuiOutlinedInput-root fieldset": { borderColor: "#fedd37" } }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Typography sx={{ fontSize: "0.75rem", color: "var(--text-secondary)" }}>°C</Typography>
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item xs={6} sm={4}>
          <TextField
            label="Seuil rouge"
            type="number"
            value={red}
            onChange={(e) => setRed(e.target.value)}
            size="small"
            fullWidth
            sx={{ ...fieldSx, "& .MuiOutlinedInput-root fieldset": { borderColor: "#fe3e26" } }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Typography sx={{ fontSize: "0.75rem", color: "var(--text-secondary)" }}>°C</Typography>
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <Button
            size="small"
            variant="contained"
            onClick={handleSave}
            disabled={saving || !isDirty}
            fullWidth
            sx={{ fontSize: "0.75rem", backgroundColor: saved ? "var(--color-primary)" : undefined }}
          >
            {saving ? (
              <CircularProgress size={14} />
            ) : saved ? (
              <CheckCircleOutline fontSize="small" />
            ) : (
              "Sauvegarder"
            )}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
}

function TemperatureThresholdsSection({ temperatureDevices, thresholds, onSaved, isMobile }) {
  const handleSaved = useCallback(
    (devEui, vals) => onSaved?.(devEui, vals),
    [onSaved]
  );

  if (!temperatureDevices || temperatureDevices.length === 0) return null;

  return (
    <Grid container spacing={1} sx={{ mt: 0.5 }}>
      <Grid item xs={12}>
        <Card
          sx={{
            borderRadius: "6px !important",
            border: "1px solid var(--border-light)",
            boxShadow: "var(--shadow-sm)",
            backgroundColor: "var(--bg-card)",
          }}
        >
          <CardContent sx={{ p: isMobile ? 1.5 : 2 }}>
        <Typography
          sx={{
            fontFamily: "var(--font-display)",
            fontSize: isMobile ? "1rem" : "1.125rem",
            fontWeight: 600,
            letterSpacing: "-0.01em",
            color: "var(--text-primary)",
            mb: 2,
          }}
        >
          Seuils de température
        </Typography>

        {temperatureDevices.map((device, idx) => (
          <React.Fragment key={device.dev_eui}>
            {idx > 0 && <Divider sx={{ my: 1.5 }} />}
            <TemperatureThresholdRow
              device={device}
              initial={thresholds?.[device.dev_eui]}
              onSaved={handleSaved}
              isMobile={isMobile}
            />
          </React.Fragment>
        ))}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
}

export default TemperatureThresholdsSection;
